import { Ionicons } from '@expo/vector-icons';
import { useLocalSearchParams } from 'expo-router';
import React, { useState } from 'react';
import { ActivityIndicator, Platform, View } from 'react-native';

import { PublicBrandHeader } from '@/components/public/public-brand-header';
import { getFirstName } from '@/components/team/member-avatar';
import { ThemedText } from '@/components/themed-text';
import { Input } from '@/components/ui/input';
import { PressableScale } from '@/components/ui/pressable-scale';
import { usePublicInvite, useSendInviteCode } from '@/hooks/use-team';
import { useAuthStore } from '@/store/authStore';
import { showToast } from '@/store/toastStore';
import { getApiErrorMessage } from '@/utils/api-error';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { KEYBOARD_BOTTOM_OFFSET } from '@/components/dashboard-scroll-view';

const BRAND = '#FD5001';

function PrimaryButton({
  label,
  onPress,
  busy,
}: {
  label: string;
  onPress: () => void;
  busy?: boolean;
}) {
  return (
    <PressableScale
      haptic
      disabled={busy}
      onPress={onPress}
      className="h-12 w-full flex-row items-center justify-center rounded-full bg-primary"
      style={{ borderCurve: 'continuous', opacity: busy ? 0.7 : 1 }}
    >
      {busy ? (
        <ActivityIndicator color="#fff" />
      ) : (
        <ThemedText className="font-bold text-sm text-white">{label}</ThemedText>
      )}
    </PressableScale>
  );
}

/**
 * Convite da equipe (`/convite/<token>`), fora do `(auth)` como o
 * `agendamento/`: o profissional recebe o link no WhatsApp, pede o código
 * para o número que o dono informou e entra direto na Agenda — sem senha.
 */
export default function InviteScreen() {
  const { token } = useLocalSearchParams<{ token: string }>();
  const { data: invite, isLoading, error: loadError } = usePublicInvite(token);
  const sendCode = useSendInviteCode(token);
  const claimInvite = useAuthStore((s) => s.claimInvite);

  const [step, setStep] = useState<'intro' | 'code'>('intro');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [claiming, setClaiming] = useState(false);

  const requestCode = async (resend = false) => {
    setError(null);
    try {
      const { devCode } = await sendCode.mutateAsync();
      if (devCode) {
        setCode(devCode);
        showToast(`Código (dev): ${devCode}`, 'info');
      } else if (resend) {
        showToast('Código reenviado!', 'success');
      }
      setStep('code');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível enviar o código agora.'));
    }
  };

  const confirm = async () => {
    if (code.replace(/\D/g, '').length !== 6) {
      setError('O código tem 6 dígitos.');
      return;
    }
    setError(null);
    setClaiming(true);
    try {
      await claimInvite({ token: token ?? '', code });
    } catch (err) {
      setError(getApiErrorMessage(err, 'Código inválido.'));
    } finally {
      setClaiming(false);
    }
  };

  const invalid = !!loadError || (invite && invite.status !== 'VALID');
  const invalidTitle =
    invite?.status === 'USED'
      ? 'Este convite já foi usado'
      : invite?.status === 'EXPIRED'
        ? 'Este convite venceu'
        : 'Convite não encontrado';

  return (
    <View className="flex-1 bg-neutral-50">
      <PublicBrandHeader />
      <KeyboardAwareScrollView
        bottomOffset={KEYBOARD_BOTTOM_OFFSET}
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{ flexGrow: 1, padding: 20, justifyContent: 'center' }}
      >
        <View className="w-full max-w-md gap-5 self-center">
          {isLoading ? (
            <ActivityIndicator size="large" color={BRAND} />
          ) : invalid || !invite ? (
            <View className="items-center gap-3">
              <Ionicons className="text-neutral-400" name="mail-unread-outline" size={44} />
              <ThemedText className="text-center font-bold text-xl text-neutral-900">
                {invalidTitle}
              </ThemedText>
              <ThemedText className="text-center text-sm leading-5 text-neutral-500">
                Peça um novo convite para quem te chamou.
              </ThemedText>
            </View>
          ) : (
            <>
              <View className="items-center gap-2">
                <View className="h-14 w-14 items-center justify-center rounded-full bg-primary/10 dark:bg-primary">
                  <Ionicons
                    className="text-primary dark:text-white"
                    name="people-outline"
                    size={26}
                  />
                </View>
                <ThemedText className="text-center font-bold text-xl leading-7 text-neutral-900">
                  {invite.organizationName} te chamou para a equipe
                </ThemedText>
                <ThemedText className="text-center text-sm leading-5 text-neutral-500">
                  Oi, {getFirstName(invite.professionalName)}! Confirme seu WhatsApp para entrar.
                </ThemedText>
              </View>

              <View
                className="gap-3 rounded-2xl border border-neutral-900/5 bg-card p-4"
                style={{ borderCurve: 'continuous' }}
              >
                {!!error && (
                  <View className="flex-row items-start gap-2 rounded-2xl bg-red-50 p-3">
                    <Ionicons className="text-error-600" name="alert-circle" size={17} />
                    <ThemedText className="flex-1 text-sm leading-5 text-red-700">
                      {error}
                    </ThemedText>
                  </View>
                )}

                <View className="flex-row items-center gap-2">
                  <Ionicons className="text-success-600" name="logo-whatsapp" size={18} />
                  <ThemedText
                    className="font-semibold text-[15px] text-neutral-800"
                    style={{ fontVariant: ['tabular-nums'] }}
                  >
                    {invite.maskedPhone}
                  </ThemedText>
                </View>

                <View style={{ display: step === 'intro' ? 'flex' : 'none' }}>
                  <PrimaryButton
                    label="Receber código no WhatsApp"
                    onPress={() => void requestCode()}
                    busy={sendCode.isPending}
                  />
                </View>

                <View className="gap-3" style={{ display: step === 'code' ? 'flex' : 'none' }}>
                  <Input
                    placeholder="000000"
                    value={code}
                    onChangeText={setCode}
                    keyboardType="number-pad"
                    maxLength={6}
                    autoComplete="one-time-code"
                    textContentType="oneTimeCode"
                    className="text-center text-2xl tracking-[8px]"
                    editable={!claiming}
                    style={Platform.OS === 'web' ? ({ outline: 'none' } as never) : undefined}
                  />
                  <PrimaryButton label="Entrar" onPress={() => void confirm()} busy={claiming} />
                  <PressableScale
                    disabled={sendCode.isPending || claiming}
                    onPress={() => {
                      setCode('');
                      void requestCode(true);
                    }}
                    className="items-center py-2"
                  >
                    <ThemedText className="font-semibold text-sm text-neutral-500">
                      Reenviar código
                    </ThemedText>
                  </PressableScale>
                </View>
              </View>

              <ThemedText className="text-center text-xs leading-4 text-neutral-400">
                Não é o seu número? Peça para quem te chamou corrigir o convite.
              </ThemedText>
            </>
          )}
        </View>
      </KeyboardAwareScrollView>
    </View>
  );
}
